import React, { useEffect, useState } from 'react';
import BoxDetail from './BoxDetail';
import RecentReservations from './RecentReservations';
import CameraStream from './CameraStream';
import CommandsExecuted from './CommandsExecuted';
import { createHotel, addBox, fetchBoxStatusOptions, updateBoxStatus } from '../utils/api';

const BOX_STATUS_OPTIONS = [
  { id: 1, label: 'Available' },
  { id: 2, label: 'Reserved' },
  { id: 3, label: 'Occupied' },
  { id: 4, label: 'Offline' }
];


const getDefaultEntryDateValue = () => {
  const now = new Date();
  const tzOffsetMs = now.getTimezoneOffset() * 60000;
  const localDate = new Date(now.getTime() - tzOffsetMs);
  return localDate.toISOString().slice(0, 16);
};

const getBoxId = (box) => box?.DHTBOX_id || box?.DHTBOXid || box?.boxId || box?.id;

const normalizeStatusOptions = (rows) => {
  if (!Array.isArray(rows)) {
    return [];
  }

  return rows
    .map((status) => ({
      id: Number(status?.DHTBST_id),
      label: status?.DHTBST_name || `Status ${status?.DHTBST_id}`
    }))
    .filter((status) => status.id && !Number.isNaN(status.id));
};

function AdminHotelPanel({ boxes = [] }) {
  const [searchBoxId, setSearchBoxId] = useState('');
  const [activeBoxId, setActiveBoxId] = useState(null);
  const [boxDetailRefreshKey, setBoxDetailRefreshKey] = useState(0);
  const [boxStatusOptions, setBoxStatusOptions] = useState(BOX_STATUS_OPTIONS);
  const [statusForm, setStatusForm] = useState({
    DHTBOX_id: '',
    DHTBST_id: 1
  });
  const [statusSubmitState, setStatusSubmitState] = useState({
    loading: false,
    error: '',
    success: ''
  });
  const [hotelForm, setHotelForm] = useState({
    name: '',
    entryDate: getDefaultEntryDateValue(),
    latitude: '',
    longitude: '',
    capacity: ''
  });
  const [hotelSubmitState, setHotelSubmitState] = useState({
    loading: false,
    error: '',
    success: ''
  });

  const [boxForm, setBoxForm] = useState({
    DHTHOT_id: 1,
    DHTBOX_length_mm: 400,

    DHTBOX_width_mm: 300,
    DHTBOX_height_mm: 200,
    DHTBOX_qr_code: '',
    DHTBOX_position: 0
  });


  const [boxSubmitState, setBoxSubmitState] = useState({
    loading: false,
    error: '',
    success: '',
    boxId: null,
    position: null
  });

  useEffect(() => {
    let isCancelled = false;

    const loadStatusOptions = async () => {
      try {
        const rows = await fetchBoxStatusOptions();
        const options = normalizeStatusOptions(rows);

        if (!isCancelled && options.length > 0) {
          setBoxStatusOptions(options);
        }
      } catch (error) {
        console.error('Failed to fetch box status options:', error);
      }
    };

    loadStatusOptions();

    return () => {
      isCancelled = true;
    };
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();

    if (!searchBoxId) {
      return;
    }

    setActiveBoxId(searchBoxId);
    setStatusForm((prev) => ({ ...prev, DHTBOX_id: searchBoxId }));
  };

  const handleHotelFieldChange = (event) => {
    const { name, value } = event.target;
    setHotelForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleBoxFieldChange = (event) => {
    const { name, value } = event.target;
    setBoxForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleStatusFieldChange = (event) => {
    const { name, value } = event.target;
    setStatusForm((prev) => ({ ...prev, [name]: value }));
  };


  const handleAddHotel = async (event) => {
    event.preventDefault();
    setHotelSubmitState({ loading: true, error: '', success: '' });

    try {
      const payload = {
        DHTHOT_name: hotelForm.name.trim(),
        DHTHST_id: 1,

        DHTHOT_entry_Date: new Date(hotelForm.entryDate).toISOString(),
        DHTHOT_latitude: Number(hotelForm.latitude),
        DHTHOT_longitude: Number(hotelForm.longitude),
        DHTHOT_capacity: Number(hotelForm.capacity)
      };

      if (!payload.DHTHOT_name) {
        throw new Error('Hotel name is required.');
      }

      if (Number.isNaN(payload.DHTHOT_latitude) || Number.isNaN(payload.DHTHOT_longitude)) {
        throw new Error('Latitude and longitude must be valid numbers.');
      }

      if (Number.isNaN(payload.DHTHOT_capacity) || payload.DHTHOT_capacity < 1) {
        throw new Error('Capacity must be at least 1.');
      }

      await createHotel(payload);

      setHotelSubmitState({ loading: false, error: '', success: 'Hotel created successfully.' });
      setHotelForm((prev) => ({
        ...prev,
        name: '',
        latitude: '',
        longitude: '',
        capacity: '',
        entryDate: getDefaultEntryDateValue()
      }));
    } catch (error) {
      setHotelSubmitState({
        loading: false,
        error: error?.message || 'Failed to create hotel.',
        success: ''
      });
    }
  };

  const handleAddBox = async (event) => {
    event.preventDefault();
    setBoxSubmitState({ loading: true, error: '', success: '', boxId: null, position: null });

    try {
      const payload = {
        DHTHOT_id: Number(boxForm.DHTHOT_id),

        DHTBOX_length_mm: Number(boxForm.DHTBOX_length_mm),
        DHTBOX_width_mm: Number(boxForm.DHTBOX_width_mm),
        DHTBOX_height_mm: Number(boxForm.DHTBOX_height_mm),
        DHTBOX_qr_code: String(boxForm.DHTBOX_qr_code || '').trim(),
        DHTBOX_position: Number(boxForm.DHTBOX_position),
      };


      if (!payload.DHTBOX_qr_code) {
        throw new Error('DHTBOX_qr_code is required.');
      }

      const numericKeys = [
        'DHTHOT_id',

        'DHTBOX_length_mm',
        'DHTBOX_width_mm',
        'DHTBOX_height_mm',
        'DHTBOX_position',
      ];


      for (const key of numericKeys) {
        if (Number.isNaN(payload[key])) {
          throw new Error(`${key} must be a valid number.`);
        }
      }

      const res = await addBox(payload);

      setBoxSubmitState({
        loading: false,
        error: '',
        success: 'Box created successfully.',
        boxId: res?.boxId ?? null,
        position: res?.position ?? null
      });

      setBoxForm((prev) => ({
        ...prev,
        DHTBOX_qr_code: ''
      }));
    } catch (error) {
      setBoxSubmitState({
        loading: false,
        error: error?.message || 'Failed to create box.',
        success: '',
        boxId: null,
        position: null
      });
    }
  };

  const handleUpdateBoxStatus = async (event) => {
    event.preventDefault();
    setStatusSubmitState({ loading: true, error: '', success: '' });

    try {
      const payload = {
        DHTBOX_id: Number(statusForm.DHTBOX_id || activeBoxId),
        DHTBST_id: Number(statusForm.DHTBST_id)
      };

      if (!payload.DHTBOX_id || Number.isNaN(payload.DHTBOX_id)) {
        throw new Error('Box ID is required.');
      }

      if (!payload.DHTBST_id || Number.isNaN(payload.DHTBST_id)) {
        throw new Error('Status is required.');
      }

      await updateBoxStatus(payload);

      setActiveBoxId(payload.DHTBOX_id);
      setStatusForm((prev) => ({ ...prev, DHTBOX_id: String(payload.DHTBOX_id) }));
      setBoxDetailRefreshKey((prev) => prev + 1);
      setStatusSubmitState({
        loading: false,
        error: '',
        success: `Box ${payload.DHTBOX_id} status updated.`
      });
    } catch (error) {
      setStatusSubmitState({
        loading: false,
        error: error?.message || 'Failed to update box status.',
        success: ''
      });
    }
  };


  return (
    <div className="row g-4 mb-5">
      <div className="col-lg-6">
        <div className="card home-card h-100">
          <div className="card-body p-3">
            <h5 className="card-title mb-3">Admin Tools</h5>
            <p className="text-muted mb-3">Search for a box in this hotel and inspect its details.</p>
            <form onSubmit={handleSearch} className="d-flex gap-2 mb-3">
              <input
                type="number"
                className="form-control home-input"
                placeholder="Box ID..."
                value={searchBoxId}
                onChange={(e) => setSearchBoxId(e.target.value)}
              />
              <button type="submit" className="btn btn-home px-4">Search</button>
            </form>
            <div className="d-flex flex-wrap gap-2">
              {boxes.map((box) => {
                const boxId = getBoxId(box);

                return (
                  <button
                    key={boxId}
                    type="button"
                    className={`btn btn-sm ${String(activeBoxId) === String(boxId) ? 'btn-light text-dark' : 'btn-outline-light'}`}
                    onClick={() => {
                      setActiveBoxId(boxId);
                      setStatusForm((prev) => ({ ...prev, DHTBOX_id: String(boxId) }));
                    }}
                  >
                    Box {boxId}
                  </button>
                );
              })}
            </div>
            {activeBoxId && <BoxDetail boxId={activeBoxId} refreshKey={boxDetailRefreshKey} />}

            <hr className="border-secondary my-4" />
            <div className="d-flex align-items-baseline justify-content-between flex-wrap gap-3 mb-3">
              <h5 className="card-title mb-0">Change Box Status</h5>
            </div>
            <form onSubmit={handleUpdateBoxStatus} className="row g-2 align-items-end">
              <div className="col-12 col-md-4">
                <label className="form-label text-white-50 mb-1">Box ID</label>
                <input
                  type="number"
                  name="DHTBOX_id"
                  className="form-control home-input"
                  value={statusForm.DHTBOX_id}
                  onChange={handleStatusFieldChange}
                  required
                  placeholder="DHTBOX_id"
                />
              </div>
              <div className="col-12 col-md-4">
                <label className="form-label text-white-50 mb-1">Status</label>
                <select
                  name="DHTBST_id"
                  className="form-select home-input"
                  value={statusForm.DHTBST_id}
                  onChange={handleStatusFieldChange}
                  required
                >
                  {boxStatusOptions.map((status) => (
                    <option key={status.id} value={status.id}>
                      {status.id} - {status.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-12 col-md-4 d-flex align-items-center gap-2">
                <button type="submit" className="btn btn-home" disabled={statusSubmitState.loading}>
                  {statusSubmitState.loading ? 'Updating...' : 'Update Status'}
                </button>
              </div>
              <div className="col-12">
                {statusSubmitState.success && <div className="text-success small">{statusSubmitState.success}</div>}
                {statusSubmitState.error && <div className="text-danger small">{statusSubmitState.error}</div>}
              </div>
            </form>

            <hr className="border-secondary my-4" />
            <div className="d-flex align-items-baseline justify-content-between flex-wrap gap-3 mb-3">
              <h5 className="card-title mb-0">Add Hotel</h5>
            </div>
            <form onSubmit={handleAddHotel} className="row g-2">
              <div className="col-12">



                <label htmlFor="hotel-title" className="form-label text-white-50 mb-1">Hotel Title</label>

                <input
                  id="hotel-title"
                  type="text"
                  name="name"
                  className="form-control home-input"
                  placeholder="Enter hotel title"
                  value={hotelForm.name}
                  onChange={handleHotelFieldChange}
                  required
                />
              </div>
              <div className="col-12">
                <input
                  type="datetime-local"
                  name="entryDate"
                  className="form-control home-input"
                  value={hotelForm.entryDate}
                  onChange={handleHotelFieldChange}
                  required
                />
              </div>
              <div className="col-md-6">
                <input
                  type="number"
                  step="any"
                  name="latitude"
                  className="form-control home-input"
                  placeholder="Latitude"
                  value={hotelForm.latitude}
                  onChange={handleHotelFieldChange}
                  required
                />
              </div>
              <div className="col-md-6">
                <input
                  type="number"
                  step="any"
                  name="longitude"
                  className="form-control home-input"
                  placeholder="Longitude"
                  value={hotelForm.longitude}
                  onChange={handleHotelFieldChange}
                  required
                />
              </div>
              <div className="col-12">
                <input
                  type="number"
                  min="1"
                  name="capacity"
                  className="form-control home-input"
                  placeholder="Capacity"
                  value={hotelForm.capacity}
                  onChange={handleHotelFieldChange}
                  required
                />
              </div>
              <div className="col-12 d-flex align-items-center gap-2">
                <button type="submit" className="btn btn-home" disabled={hotelSubmitState.loading}>
                  {hotelSubmitState.loading ? 'Adding...' : 'Add Hotel'}
                </button>
                {hotelSubmitState.success && <span className="text-success small">{hotelSubmitState.success}</span>}
                {hotelSubmitState.error && <span className="text-danger small">{hotelSubmitState.error}</span>}
              </div>
            </form>

            <hr className="border-secondary my-4" />
            <div className="d-flex align-items-baseline justify-content-between flex-wrap gap-3 mb-3">
              <h5 className="card-title mb-0">Add Box</h5>
            </div>

            <form onSubmit={handleAddBox} className="row g-3 align-items-end">
              <div className="col-12 col-md-4">
                <label className="form-label text-white-50 mb-1">Hotel ID</label>
                <input
                  type="number"
                  className="form-control home-input"
                  name="DHTHOT_id"
                  value={boxForm.DHTHOT_id}
                  onChange={handleBoxFieldChange}
                  required
                  placeholder="DHTHOT_id"
                />
              </div>

              <div className="col-12 col-md-4">
                <label className="form-label text-white-50 mb-1">Length (mm)</label>
                <input
                  type="number"
                  className="form-control home-input"
                  name="DHTBOX_length_mm"
                  value={boxForm.DHTBOX_length_mm}
                  onChange={handleBoxFieldChange}
                  required
                  placeholder="Length (mm)"
                />
              </div>

              <div className="col-12 col-md-4">
                <label className="form-label text-white-50 mb-1">Width (mm)</label>
                <input
                  type="number"
                  className="form-control home-input"
                  name="DHTBOX_width_mm"
                  value={boxForm.DHTBOX_width_mm}
                  onChange={handleBoxFieldChange}
                  required
                  placeholder="Width (mm)"
                />
              </div>

              <div className="col-12 col-md-4">
                <label className="form-label text-white-50 mb-1">Height (mm)</label>
                <input
                  type="number"
                  className="form-control home-input"
                  name="DHTBOX_height_mm"
                  value={boxForm.DHTBOX_height_mm}
                  onChange={handleBoxFieldChange}
                  required
                  placeholder="Height (mm)"
                />
              </div>

              <div className="col-12 col-md-8">
                <label className="form-label text-white-50 mb-1">QR Code</label>
                <input
                  type="text"
                  className="form-control home-input"
                  name="DHTBOX_qr_code"
                  value={boxForm.DHTBOX_qr_code}
                  onChange={handleBoxFieldChange}
                  required
                  placeholder="DHTBOX_qr_code (e.g. QR-BOX-001)"
                />
              </div>

              <div className="col-12 col-md-6">
                <label className="form-label text-white-50 mb-1">Position</label>
                <input
                  type="number"
                  className="form-control home-input"
                  name="DHTBOX_position"
                  value={boxForm.DHTBOX_position}
                  onChange={handleBoxFieldChange}
                  required
                  placeholder="DHTBOX_position"
                />
              </div>

              <div className="col-12 col-md-6 d-flex align-items-center gap-2">
                <button type="submit" className="btn btn-home" disabled={boxSubmitState.loading}>
                  {boxSubmitState.loading ? 'Adding...' : 'Add Box'}
                </button>
                <div className="flex-grow-1">
                  {boxSubmitState.success && (
                    <div className="text-success small">
                      {boxSubmitState.success}
                      {boxSubmitState.boxId !== null && (
                        <> (boxId: {boxSubmitState.boxId}, position: {boxSubmitState.position})</>
                      )}
                    </div>
                  )}
                  {boxSubmitState.error && <div className="text-danger small">{boxSubmitState.error}</div>}
                </div>
              </div>
            </form>

          </div>
        </div>
      </div>

      <div className="col-lg-6">
        <div className="row g-4 h-100">

          <div className="col-12">
            <RecentReservations />
          </div>
          <div className="col-12">
            <CommandsExecuted />
          </div>
          <div className="col-12">
            <CameraStream boxes={boxes} />
          </div>

        </div>
      </div>
    </div>
  );
}

export default AdminHotelPanel;
